import { SVGProps, Text, View } from "@react-pdf/renderer";
import ReactMarkdown, { Options } from "react-markdown";

import {
  intakeQuestionDataByCodes,
  removeDot,
  renderQuestionTitle,
  replaceUnderscoresUpperCase,
} from "~/common/utils";
import {
  DIRECT_TO_INDIRECT_TO_SWATCH_MAP,
  INDIRECT_TO_SWATCH_MAP,
} from "~/providers/utils/getPreviewFunctionalDomainsIcon";
import {
  DirectInquiryQuestions,
  FUNCTIONAL_DOMAIN_KEY,
  FunctionalDomainKeys,
  FunctionalDomainsV2,
} from "~/providers/utils/types/patientIntakePreviewTypes";
import {
  EnjoymentIcon,
  FamilyIcon,
  HealthIcon,
  HopeIcon,
  MoneyIcon,
  MoodIcon,
  PostpartumIcon,
  RelationshipsIcon,
  SocialSupportIcon,
  WorkIcon,
} from "../assets";
import { styles } from "../styles";

const DOMAIN_TO_ICON_MAP = {
  [FUNCTIONAL_DOMAIN_KEY.enjoyment]: EnjoymentIcon,
  [FUNCTIONAL_DOMAIN_KEY.family]: FamilyIcon,
  [FUNCTIONAL_DOMAIN_KEY.health]: HealthIcon,
  [FUNCTIONAL_DOMAIN_KEY.hope]: HopeIcon,
  [FUNCTIONAL_DOMAIN_KEY.money]: MoneyIcon,
  [FUNCTIONAL_DOMAIN_KEY.mood]: MoodIcon,
  [FUNCTIONAL_DOMAIN_KEY.post_partum]: PostpartumIcon,
  [FUNCTIONAL_DOMAIN_KEY.relationships]: RelationshipsIcon,
  [FUNCTIONAL_DOMAIN_KEY.social_support]: SocialSupportIcon,
  [FUNCTIONAL_DOMAIN_KEY.work]: WorkIcon,
};

const DomainIcon = ({
  domain,
  ...props
}: SVGProps & { domain: FunctionalDomainKeys }) => {
  const Icon = DOMAIN_TO_ICON_MAP[domain];

  return (
    <Icon {...props} style={{ borderRadius: 4, ...(props.style ?? {}) }} />
  );
};

const DirectQuestions = ({
  questions,
}: {
  questions: DirectInquiryQuestions[];
}) => {
  if (!questions?.length) return <Text>None</Text>;

  return questions.map((question) => {
    const answer = question.answer;
    const questionData = intakeQuestionDataByCodes[question.question_code];
    const questionTitle = renderQuestionTitle(questionData.title);
    return (
      <Text key={question.question_code}>
        {questionTitle && removeDot(questionTitle)}:{" "}
        <Text style={{ color: "#4C5560", fontWeight: 600 }}>
          {answer?.value}
        </Text>
      </Text>
    );
  });
};

const IndirectQuestions = ({ sentences }: { sentences: string[] }) => {
  if (!sentences?.length) return <Text style={{ color: "#4C5560" }}>None</Text>;

  const components: Options["components"] = {
    em({ children }) {
      return (
        <Text style={{ fontWeight: 600, color: "#4C5560" }}>{children}</Text>
      );
    },
    p({ children }) {
      return <Text style={{ color: "#4C5560" }}>{children}</Text>;
    },
    span({ children }) {
      return <Text style={{ color: "#4C5560" }}>{children}</Text>;
    },
    i({ children }) {
      return <Text style={{ color: "#4C5560" }}>{children}</Text>;
    },
    u({ children }) {
      return <Text style={{ color: "#4C5560" }}>{children}</Text>;
    },
    li({ children }) {
      return (
        <View style={{ width: "100%", marginBottom: 4 }}>
          <View style={{ flexDirection: "row", width: "100%" }}>
            <Text style={{ marginRight: 4 }}>•</Text>
            <Text>{children}</Text>
          </View>
        </View>
      );
    },
  };

  return sentences.map((sentence, i) => {
    return (
      <Text key={i} style={{ color: "#4C5560" }}>
        <ReactMarkdown components={components}>
          {sentence.replace(/\W[.][.][.]/g, "")}
        </ReactMarkdown>
      </Text>
    );
  });
};

export const ConsultationExportSummaryDomains = ({
  domains,
}: {
  domains: FunctionalDomainsV2[];
}) => {
  if (!domains.length) return null;

  return (
    <View style={{ flexDirection: "column", rowGap: 10 }}>
      {domains.map((domain) => (
        <View key={domain.section}>
          <View
            style={{
              flexDirection: "column",
              marginBottom: 4,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                columnGap: 8,
              }}
            >
              <DomainIcon
                domain={domain.section}
                width={24}
                fill={
                  DIRECT_TO_INDIRECT_TO_SWATCH_MAP[
                    domain.direct_inquiry.classification
                  ][domain.indirect_inquiry.classification].primary
                }
                stroke={
                  DIRECT_TO_INDIRECT_TO_SWATCH_MAP[
                    domain.direct_inquiry.classification
                  ][domain.indirect_inquiry.classification].primary
                }
                style={{
                  backgroundColor:
                    DIRECT_TO_INDIRECT_TO_SWATCH_MAP[
                      domain.direct_inquiry.classification
                    ][domain.indirect_inquiry.classification].secondary,
                }}
              />
              <View>
                <Text
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    lineHeight: 1,
                    color:
                      DIRECT_TO_INDIRECT_TO_SWATCH_MAP[
                        domain.direct_inquiry.classification
                      ][domain.indirect_inquiry.classification].primary,
                  }}
                >
                  {domain.may_indicate}
                </Text>
                <Text
                  style={[
                    styles.h3,
                    { fontFamily: "Outfit", fontWeight: 600, marginBottom: 0 },
                  ]}
                >
                  {replaceUnderscoresUpperCase(domain.section)}
                </Text>
              </View>
            </View>

            {domain.narrative && (
              <Text style={{ marginTop: 4 }}>{domain.narrative}</Text>
            )}
          </View>
          <View
            key={domain.section}
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              marginLeft: -4,
              marginRight: -4,
            }}
          >
            <View
              key={domain.section}
              style={{
                width: "50%",
                padding: 4,
                flexDirection: "column",
              }}
              wrap={false}
            >
              <View
                style={{
                  backgroundColor: "#F0F4FE",
                  flexGrow: 1,
                  borderRadius: 6,
                  paddingHorizontal: 12,
                  paddingVertical: 8,
                }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 8,
                  }}
                >
                  <Text style={{ fontWeight: 600 }}>Direct inquiry</Text>
                  <Text
                    style={{
                      fontWeight: 600,
                      color:
                        INDIRECT_TO_SWATCH_MAP[
                          domain.direct_inquiry.classification
                        ].primary,
                    }}
                  >
                    {replaceUnderscoresUpperCase(
                      domain.direct_inquiry.classification,
                    ).replace("N/a", "N/A")}
                  </Text>
                </View>
                <DirectQuestions
                  questions={domain.direct_inquiry.questions ?? []}
                />
              </View>
            </View>
            <View
              key={domain.section}
              style={{
                width: "50%",
                padding: 4,
                flexDirection: "column",
              }}
              wrap={false}
            >
              <View
                style={{
                  backgroundColor: "#F0F4FE",
                  flexGrow: 1,
                  borderRadius: 6,
                  paddingHorizontal: 12,
                  paddingVertical: 8,
                }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 8,
                  }}
                >
                  <Text style={{ fontWeight: 600 }}>Indirect inquiry</Text>
                  <Text
                    style={{
                      fontWeight: 600,
                      color:
                        INDIRECT_TO_SWATCH_MAP[
                          domain.indirect_inquiry.classification
                        ].primary,
                    }}
                  >
                    {replaceUnderscoresUpperCase(
                      domain.indirect_inquiry.classification,
                    )}
                  </Text>
                </View>
                <IndirectQuestions
                  sentences={domain.indirect_inquiry.sentences ?? []}
                />
              </View>
            </View>
          </View>
        </View>
      ))}
    </View>
  );
};
